import {
	CognitoUser as AWSCognitoUser, CognitoIdToken,
	CognitoAccessToken, CognitoRefreshToken, CognitoUserSession, IMfaSettings
} from "amazon-cognito-identity-js";
import { createUserData } from "./aws-sdk-helper";

import { CognitoAuthorizerPayload } from "@impulse/sdp-shared-models";

import { LoggedInUser } from "./user";
import { StoredCognitoUser, AssociateSoftwareTokenResult } from "./models";

export class CognitoUser extends LoggedInUser {

	private awsCognitoUser: AWSCognitoUser;
	private idToken: CognitoIdToken;
	private accessToken: CognitoAccessToken;
	private refreshToken: CognitoRefreshToken;

	constructor(awsCognitoUser: AWSCognitoUser,
		idToken: CognitoIdToken,
		accessToken: CognitoAccessToken,
		refreshToken: CognitoRefreshToken);

	constructor(data: StoredCognitoUser);

	constructor(
		userData: AWSCognitoUser | StoredCognitoUser,
		idToken?: CognitoIdToken,
		accessToken?: CognitoAccessToken,
		refreshToken?: CognitoRefreshToken
	) {
		super();
		if (userData instanceof AWSCognitoUser) {
			this.awsCognitoUser = userData;
			this.idToken = idToken!;
			this.accessToken = accessToken!;
			this.refreshToken = refreshToken!;
		} else {
			this.awsCognitoUser = createUserData(userData.userData.user.email);
			this.idToken = new CognitoIdToken(<any>{ IdToken: userData.idJWTToken });
			this.accessToken = new CognitoAccessToken(<any>{ AccessToken: userData.accessJWTToken });
			this.refreshToken = new CognitoRefreshToken(<any>{ RefreshToken: userData.refreshJWTToken });
			this.setFieldsFromUserData(userData.userData);
			/*
			just making a new user isn't enough to flag this as a logged-in user for some cognito APIs
			for example, resetPassword fails with a "User is not authenticated" error when attempting to use this object
			this error comes locally, it makes no attempt to actaully use the user to contact cognito
			we'll poke the object enough to get it's internal state to think it's authenticated
			experimentally, getSession appears to be enough
			*/
			this.awsCognitoUser.getSession((error: Error, session: CognitoUserSession) => {
				/*
				this is attempting to recover the session information from the local storage variables that amazon controls
				if this errors something horrible has happened and we should probably show them the login page
				for now, we'll do nothing, as other code should already be handling those cases
				just invoking this method and ignoring the result is enough to get the various tokens into this and into awsCognitoUser
				*/
			});
		}
		this.type = "cognito";
	}

	public refreshSession() {
		return new Promise<CognitoUser>((resolve, reject) => {
			this.awsCognitoUser.refreshSession(this.refreshToken, (err, result: CognitoUserSession) => {
				if (err) {
					reject(err);
				} else {
					this.idToken = result.getIdToken();
					this.accessToken = result.getAccessToken();
					this.refreshToken = result.getRefreshToken();
					resolve(this);
				}
			});
		});
	}

	public isExpired() {
		const expiry = new Date(this.accessToken.getExpiration() * 1000);
		const now = new Date();
		return expiry < now;
	}

	public getAuthToken(): string {
		const tokenPayload: CognitoAuthorizerPayload = {
			jwt: this.accessToken.getJwtToken(),
			type: "cognito"
		};
		return Buffer.from(JSON.stringify(tokenPayload)).toString("base64");
	}

	public signOut() {
		this.awsCognitoUser.signOut();
	}

	public getAWSCognitoUser() {
		return this.awsCognitoUser;
	}

	public resetPassword(oldPassword: string, newPassword: string) {
		return new Promise<void>((resolve, reject) => {
			this.awsCognitoUser.changePassword(oldPassword, newPassword, async (error, result) => {
				if (error) {
					if (error.message === "Attempt limit exceeded, please try after some time.") {
						reject(new Error("resetPassword.attemptLimitExceeded"));
					} else {
						reject(new Error("resetPassword.genericError"));
					}
				} else {
					resolve();
				}
			});
		});
	}

	public associateSoftwareToken(): Promise<AssociateSoftwareTokenResult> {
		return new Promise<AssociateSoftwareTokenResult>((resolve, reject) => {
			this.awsCognitoUser.associateSoftwareToken({
				associateSecretCode: (secretCode) => {
					resolve({ secretCode: secretCode });
				},
				onFailure: (err) => {
					reject(err);
				}
			});
		});
	}

	public verifySoftwareToken(totpCode: string, deviceName: string): Promise<CognitoUserSession> {
		return new Promise<CognitoUserSession>((resolve, reject) => {
			this.awsCognitoUser.verifySoftwareToken(totpCode, deviceName, {
				onSuccess: (session: CognitoUserSession) => {
					resolve(session);
				},
				onFailure: (err) => {
					reject(err);
				}
			});
		});
	}

	public confirmMfaSetup(): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const totpMfaSettings = {
				PreferredMfa: true,
				Enabled: true
			};
			// must pass null sms settings or we will get an error from cognito
			// tslint:disable-next-line:no-null-keyword
			const smsSettings: IMfaSettings | null = null;
			this.awsCognitoUser.setUserMfaPreference(smsSettings!, totpMfaSettings, (err, result) => {
				if (!!err) {
					reject(err);
				} else {
					resolve();
				}
			});
		});
	}

	public async disableMfa() {
		this.awsCognitoUser.disableMFA((err, res) => {
			if (!!err) {
				throw new Error("error disabling mfa");
			} else {
				const totpMfaSettings = {
					PreferredMfa: false,
					Enabled: false
				};
				// must pass null sms settings or we will get an error from cognito
				// tslint:disable-next-line:no-null-keyword
				const smsSettings: IMfaSettings | null = null;
				this.awsCognitoUser.setUserMfaPreference(smsSettings!, totpMfaSettings, (e, result) => {
					if (!!e) {
						throw new Error(e.message);
					}
				});
			}
		});
	}

	public toJSONString() {
		const data: StoredCognitoUser = {
			userData: this.getUserData(),
			idJWTToken: this.idToken.getJwtToken(),
			accessJWTToken: this.accessToken.getJwtToken(),
			refreshJWTToken: this.refreshToken.getToken(),
			type: "cognito"
		};
		return JSON.stringify(data);
	}

}

export function isCognitoUser(user: LoggedInUser): user is CognitoUser {
	return user.type === "cognito";
}
